import {
  AbstractControl,
  FormGroupDirective,
  NgForm,
  ValidatorFn,
  FormControl,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import moment from 'moment';
import { Observable, OperatorFunction } from 'rxjs';
import { delay, filter, retryWhen, scan } from 'rxjs/operators';

/**
 * Test IE browser
 */
export const isIE = (): boolean => {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');

  return msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./);
};

/**
 * FormControl validator - checks if value is unique
 *
 * @param arrayOfNames
 */
export const uniqueNameValidator = (arrayOfNames: string[]): ValidatorFn => (
  control: AbstractControl
): { [key: string]: any } | null => {
  const arrayOfLowerCaseNames = [];
  arrayOfNames.map((name) => {
    if (name) {
      arrayOfLowerCaseNames.push(name.trim().toLocaleLowerCase());
    }
  });
  if (arrayOfLowerCaseNames.includes(control.value.trim().toLocaleLowerCase())) {
    return { notUnique: true };
  }

  return null;
};

/**
 * FormControl validator - checks if difference between value and given startDate value equals given daysDifference
 */
export function endDateValidator(startDate: any, daysDifference: number): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!moment.isMoment(startDate.value) || !moment.isMoment(control.value)) {
      return null;
    }
    const isSameOrAfter = control.value.diff(startDate.value, 'd') >= daysDifference;

    return !isSameOrAfter ? { minEndDate: { value: control.value } } : null;
  };
}

/**
 * FormControl validator - checks if string is not empty after trim
 */
export const noWhitespaceValidator = (): ValidatorFn => (
  control: AbstractControl
): { [key: string]: any } | null => {
  const isWhitespace = (control.value || '').trim().length === 0;

  return isWhitespace ? { whitespace: true } : null;
};

/** Error when invalid control is dirty, touched, or submitted. It fixes showing error only after blur issue */
export class ImmediateStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl<any> | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;

    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}
/** Error when control is invalid */
export class DatePickerErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl<any> | null,
    _form: FormGroupDirective | NgForm | null
  ): boolean {
    return control && control.invalid;
  }
}

// retry only server error responses (500–599)
// most important being 504 gateway timeout
export function retryFailedCall<T>(retryCount: number): OperatorFunction<T, T> {
  return (inputObservable: Observable<T>): Observable<T> =>
    inputObservable.pipe(
      retryWhen((error) =>
        error.pipe(
          filter((theerror) => {
            if (!theerror) {
              return true;
            }
            const firstDigit = theerror.statusCode?.toString()[0];

            if (firstDigit === '5') {
              return true;
            } else {
              // if error code does not start with a '5' then break out of the retry mechanism by throwing error to the outer stream
              throw theerror;
            }
          }),
          scan((retryIndex, _error) => {
            if (retryIndex === retryCount) {
              throw _error;
            }
            console.log(`Call to the backend has failed, retry: ${retryIndex + 1}/${retryCount}`);

            return retryIndex + 1;
          }, 1),
          delay(1000)
        )
      )
    );
}
